import {
	Box,
	ButtonBase,
	InputBase,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { ChevronDown, Search } from "lucide-react";
import { type KeyboardEvent, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { VALUE_LINE_HEIGHT } from "web_myplann/Shared/Components/FlatRow/constants";
import type { FlatRowSelectOption } from "web_myplann/Shared/Components/FlatRow/FlatRowSelect";
import { FlatRowSelectOptionList } from "web_myplann/Shared/Components/FlatRow/FlatRowSelectOptionList";
import { FlatRowSelectOptionListSkeleton } from "web_myplann/Shared/Components/FlatRow/FlatRowSelectOptionListSkeleton";
import { FlatRowTriggerButton } from "web_myplann/Shared/Components/FlatRow/FlatRowTriggerButton";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

export type FlatRowComboboxProps = {
	label: string;
	value: string;
	options: FlatRowSelectOption<string>[];
	onChange: (value: string) => void;
	onBlur?: () => void;
	drawerTitle?: string;
	placeholder?: string;
	searchPlaceholder?: string;
	isLast?: boolean;
	isLoading?: boolean;
	error?: boolean;
	helperText?: string;
};

export const FlatRowCombobox = ({
	label,
	value,
	options,
	onChange,
	onBlur,
	drawerTitle,
	placeholder,
	searchPlaceholder,
	isLast,
	isLoading,
	error,
	helperText,
}: FlatRowComboboxProps) => {
	const theme = useTheme();
	const { t } = useResource();
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const openDrawer = () => {
		setSearch("");
		setOpen(true);
	};

	const closeDrawer = () => {
		setOpen(false);
		onBlur?.();
	};

	const commit = (next: string) => {
		onChange(next.trim());
		setSearch("");
		setOpen(false);
		onBlur?.();
	};

	const normalizedSearch = search.trim().toLowerCase();

	const filteredOptions = normalizedSearch
		? options.filter((option) =>
				option.label.toLowerCase().includes(normalizedSearch),
			)
		: options;

	const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (isLoading) {
			return;
		}
		if (event.key === "Enter" && search.trim()) {
			event.preventDefault();
			commit(search);
		}
	};

	return (
		<Box>
			<FlatRowTriggerButton
				onClick={openDrawer}
				sx={{
					borderBottom: isLast
						? "none"
						: `1px solid ${theme.palette.surface.border}`,
				}}
			>
				<Typography
					variant="body2"
					color={error ? theme.palette.error.main : theme.palette.grey[500]}
					flexShrink={0}
					mr={2}
					lineHeight={`${VALUE_LINE_HEIGHT}px`}
				>
					{label}
				</Typography>
				<Stack
					direction="row"
					alignItems="flex-start"
					justifyContent="flex-end"
					gap={0.6}
					flex={1}
					minWidth={0}
				>
					<Typography
						variant="bodyMedium"
						color={value ? theme.palette.text.primary : theme.palette.grey[300]}
						sx={{
							fontWeight: value ? undefined : 400,
							minWidth: 0,
							textAlign: "right",
							lineHeight: `${VALUE_LINE_HEIGHT}px`,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{value || placeholder || ""}
					</Typography>
					<Stack
						direction="row"
						alignItems="center"
						flexShrink={0}
						height={`${VALUE_LINE_HEIGHT}px`}
					>
						<ChevronDown
							size={theme.iconSize.sm}
							color={theme.palette.grey[400]}
						/>
					</Stack>
				</Stack>
			</FlatRowTriggerButton>

			{error && helperText && (
				<Typography
					component="p"
					variant="caption"
					color={theme.palette.error.main}
					px={theme.spacingTokens.lg}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}

			<MyPlannDrawer
				anchor="bottom"
				open={open}
				onClose={closeDrawer}
				title={drawerTitle ?? label}
				grabber
			>
				<Stack
					direction="row"
					alignItems="center"
					gap={theme.spacingTokens.sm}
					flexShrink={0}
					mx={theme.spacingTokens.lg}
					mb={theme.spacingTokens.xs}
					px={theme.spacingTokens.md}
					py={theme.spacingTokens.sm}
					borderRadius={theme.radii.md}
					bgcolor={theme.palette.grey[100]}
				>
					<Box display="flex" flexShrink={0}>
						<Search size={theme.iconSize.sm} color={theme.palette.grey[400]} />
					</Box>
					<InputBase
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						onKeyDown={handleSearchKeyDown}
						placeholder={searchPlaceholder ?? placeholder ?? label}
						fullWidth
						sx={{
							fontSize: theme.typography.body2.fontSize,
							color: theme.palette.grey[900],
							"& input": { p: 0, textOverflow: "ellipsis" },
							"& input::placeholder": {
								color: theme.palette.grey[400],
								opacity: 1,
							},
						}}
					/>
				</Stack>

				<Stack
					flex="1 1 auto"
					minHeight={0}
					overflow="auto"
					pb={theme.spacingTokens.xl}
				>
					{isLoading ? (
						<FlatRowSelectOptionListSkeleton />
					) : (
						<>
							{value && !search.trim() && (
								<ButtonBase
									onClick={() => commit("")}
									sx={{
										mx: theme.spacingTokens.lg,
										py: theme.spacingTokens.md,
										justifyContent: "flex-start",
										textAlign: "left",
										borderBottom: `1px solid ${theme.palette.surface.border}`,
									}}
								>
									<Typography
										variant="bodyMedium"
										color={theme.palette.grey[500]}
									>
										{t(Resources.Portfolio.AddShared.Combobox.noSelection)}
									</Typography>
								</ButtonBase>
							)}

							{search.trim() && (
								<ButtonBase
									onClick={() => commit(search)}
									sx={{
										mx: theme.spacingTokens.lg,
										py: theme.spacingTokens.md,
										justifyContent: "flex-start",
										textAlign: "left",
										borderBottom: `1px solid ${theme.palette.surface.border}`,
									}}
								>
									<Typography
										variant="bodyMedium"
										color={theme.palette.blue[600]}
									>
										{t(Resources.Portfolio.AddShared.Combobox.useValue, {
											value: search.trim(),
										})}
									</Typography>
								</ButtonBase>
							)}

							<Stack pt={theme.spacingTokens.xs}>
								<FlatRowSelectOptionList
									options={filteredOptions}
									value={value === "" ? [] : [value]}
									onSelect={commit}
								/>
							</Stack>
						</>
					)}
				</Stack>
			</MyPlannDrawer>
		</Box>
	);
};
